import React, { useState, useEffect, useRef } from 'react';
import { User, Edit2, Save, X, Mail, Phone, MapPin, Check, Upload, Lock, Eye, EyeOff } from 'lucide-react';
import Header from '../components/Header';
import Sidebar from '../components/Sidebar';

const Profile = () => {
  const [isEditing, setIsEditing] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [showPasswordSuccess, setShowPasswordSuccess] = useState(false);
  const [formData, setFormData] = useState({
    fullName: 'John Doe',
    email: 'john.doe@email.com',
    mobile: '+91 9876543210',
    address: '123 Main Street, Ward 5, Balurghat'
  });
  const [passwordData, setPasswordData] = useState({
    newPassword: '',
    confirmPassword: ''
  });
  const [passwordErrors, setPasswordErrors] = useState({});
  const [showPasswords, setShowPasswords] = useState({
    new: false,
    confirm: false
  });
  const [originalData, setOriginalData] = useState({ ...formData });
  const [errors, setErrors] = useState({});
  const [studentId, setStudentId] = useState('');
  const [profileImage, setProfileImage] = useState(null);
  const [isHoveringAvatar, setIsHoveringAvatar] = useState(false);
  const [isVerified, setIsVerified] = useState(false);
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [isSendingOtp, setIsSendingOtp] = useState(false);
  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const fileInputRef = useRef(null);
  const otpInputRefs = [useRef(null), useRef(null), useRef(null), useRef(null), useRef(null), useRef(null)];

  const handleEdit = () => {
    setOriginalData({ ...formData });
    setIsEditing(true);
    setErrors({});
  };

  const handleCancel = () => {
    setFormData({ ...originalData });
    setIsEditing(false);
    setErrors({});
  };

  const handleSave = async () => {
    const newErrors = {};
    
    if (!formData.fullName.trim()) {
      newErrors.fullName = 'Full name is required';
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }
    
    if (!formData.mobile.trim()) {
      newErrors.mobile = 'Mobile number is required';
    } else if (!/^[+]?[\d\s\-()]+$/.test(formData.mobile)) {
      newErrors.mobile = 'Mobile number is invalid';
    }
    
    if (!formData.address.trim()) {
      newErrors.address = 'Address is required';
    }
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    
    const updateData = {
      fullname: formData.fullName,
      email: formData.email,
      mobile_no: formData.mobile,
      address: formData.address
    };
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}api/student-profile-update`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updateData)
      });
      if (response.ok) {
        setOriginalData({ ...formData });
        setIsEditing(false);
        setShowSuccessMessage(true);
        setTimeout(() => setShowSuccessMessage(false), 3000);
      } else {
        console.error('Update failed');
      }
    } catch (error) {
      console.error('Error updating profile:', error);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
    if (errors[field]) {
      setErrors({ ...errors, [field]: '' });
    }
  };

  const handleAvatarClick = () => {
    fileInputRef.current.click();
  };

  const handleFileChange = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    const formDataUpload = new FormData();
    formDataUpload.append('image', file);
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}api/student-image-update`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formDataUpload
      });
      if (response.ok) {
        const data = await response.json();
        setProfileImage(data.image);
        setShowSuccessMessage(true);
        setTimeout(() => setShowSuccessMessage(false), 3000);
      } else {
        console.error('Upload failed');
      }
    } catch (error) {
      console.error('Error uploading image:', error);
    }
  };

  const handleSendOtp = async () => {
    setIsSendingOtp(true);
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}api/send-otp`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email: formData.email })
      });
      if (response.ok) {
        setShowOtpModal(true);
      } else {
        console.error('Failed to send OTP');
      }
    } catch (error) {
      console.error('Error sending OTP:', error);
    } finally {
      setIsSendingOtp(false);
    }
  };

  const handleOtpChange = (index, value) => {
    if (value.length > 1) return;
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    
    if (value && index < 5) {
      otpInputRefs[index + 1].current?.focus();
    }
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      otpInputRefs[index - 1].current?.focus();
    }
  };

  const handleVerifyOtp = async () => {
    const otpString = otp.join('');
    if (otpString.length !== 6) {
      console.error('Please enter all 6 digits');
      return;
    }
    
    setIsVerifyingOtp(true);
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}api/verify-otp`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email: formData.email, otp: otp.join('') })
      });
      if (response.ok) {
        setIsVerified(true);
        setShowOtpModal(false);
        setShowSuccessMessage(true);
        setTimeout(() => setShowSuccessMessage(false), 3000);
      } else {
        console.error('Failed to verify OTP');
      }
    } catch (error) {
      console.error('Error verifying OTP:', error);
    } finally {
      setIsVerifyingOtp(false);
    }
  };

  const handleCloseOtpModal = () => {
    setShowOtpModal(false);
    setOtp(['', '', '', '', '', '']);
  };

  const handlePasswordInputChange = (field, value) => {
    setPasswordData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error for this field when user starts typing
    if (passwordErrors[field]) {
      setPasswordErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  const validatePasswordForm = () => {
    const errors = {};
    
    if (!passwordData.newPassword.trim()) {
      errors.newPassword = 'New password is required';
    } else if (passwordData.newPassword.length < 6) {
      errors.newPassword = 'Password must be at least 6 characters long';
    }
    
    if (!passwordData.confirmPassword.trim()) {
      errors.confirmPassword = 'Please confirm your new password';
    } else if (passwordData.newPassword !== passwordData.confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }
    
    setPasswordErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handlePasswordChange = async () => {
    if (!validatePasswordForm()) return;
    
    setIsChangingPassword(true);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('password', passwordData.newPassword);
      formData.append('confirm_password', passwordData.confirmPassword);

      const response = await fetch(`${process.env.REACT_APP_API_URL}api/student-change-password`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formData
      });
      
      const result = await response.json();

      if (response.ok && result.status === 'success') {
        setShowPasswordSuccess(true);
        setPasswordData({
          newPassword: '',
          confirmPassword: ''
        });
        setTimeout(() => setShowPasswordSuccess(false), 3000);
      } else {
        setPasswordErrors({
          newPassword: result.message || 'Failed to change password'
        });
      }
    } catch (error) {
      setPasswordErrors({
        newPassword: 'An error occurred while changing password'
      });
    } finally {
      setIsChangingPassword(false);
    }
  };

  const fetchProfile = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      console.error('No token found');
      return;
    }
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}api/student-profile`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      const data = await response.json();
      if (data.status === 'success') {
        setFormData({
          fullName: data.data.fullname,
          email: data.data.email,
          mobile: data.data.mobile_no || '',
          address: data.data.address || ''
        });
        setOriginalData({
          fullName: data.data.fullname,
          email: data.data.email,
          mobile: data.data.mobile_no || '',
          address: data.data.address || ''
        });
        setStudentId(data.data.registration_no);
        setProfileImage(data.data.image);
        setIsVerified(data.data.is_verified === 1);
      } else {
        console.error('Failed to fetch profile');
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
    }
  };

  useEffect(() => {
    fetchProfile();
  }, []);

  return (
    <div className="dashboard-container">
      <Header />
      <div className="dashboard-wrapper">
        <Sidebar activeItem="profile" />
        <div className="flex-grow-1 dashboard-content">
          <h2 className="page-title mb-4">My Profile</h2>
          
          {showSuccessMessage && (
            <div className="alert alert-success alert-dismissible fade show" role="alert">
              <Check size={16} className="me-2" />
              Profile updated successfully!
              <button type="button" className="btn-close" onClick={() => setShowSuccessMessage(false)}></button>
            </div>
          )}

          <div className="card shadow-sm border profile-card-enhanced">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-start gap-3 mb-4">
                <div className="profile-header">
                  <div className="d-flex align-items-center">
                    <div className="profile-avatar-enhanced me-3" onClick={handleAvatarClick} onMouseEnter={() => setIsHoveringAvatar(true)} onMouseLeave={() => setIsHoveringAvatar(false)} style={{cursor: 'pointer', position: 'relative'}}>
                      {profileImage ? (
                        <img src={profileImage} alt="Profile" style={{width: '50px', height: '50px', borderRadius: '50%'}} />
                      ) : (
                        <User className="text-white" size={40} />
                      )}
                      {isHoveringAvatar && (
                        <div style={{position: 'absolute', top: 5, left: 5, width: '50px', height: '50px', backgroundColor: 'rgba(0,0,0,0.5)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                          <Upload size={20} className="text-white" />
                        </div>
                      )}
                    </div>
                    <div>
                      <h3 className="h5 mb-1 fw-bold" style={{textTransform:'capitalize'}}>{formData.fullName}</h3>
                      <p className="mb-1">Student ID: {studentId}</p>
                      <div className="d-flex align-items-center">
                        <div className="verification-dot me-2"></div>
                        {isVerified ? (
                          <div className="success-badge">Verified Account</div>
                        ) : (
                          <div className="info-badge pending-badge">Pending Verification</div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="profile-actions-enhanced">
                  {!isEditing ? (
                    <button className="btn-edit-enhanced" onClick={handleEdit}>
                      <Edit2 size={14} />
                    </button>
                  ) : (
                    <div className="d-flex gap-1">
                      <button className="btn-save-enhanced" onClick={handleSave}>
                        <Save size={14} />
                      </button>
                      <button className="btn-cancel-enhanced" onClick={handleCancel}>
                        <X size={14} />
                      </button>
                    </div>
                  )}
                </div>
              </div>
              
              <div className="row">
                <div className="col-md-6 mb-3">
                  <label className="form-label fw-medium">
                    <User size={16} className="me-2" />
                    Full Name
                  </label>
                  {isEditing ? (
                    <div>
                      <input
                        type="text"
                        className={`form-control ${errors.fullName ? 'is-invalid' : ''}`}
                        value={formData.fullName}
                        onChange={(e) => handleInputChange('fullName', e.target.value)}
                      />
                      {errors.fullName && <div className="invalid-feedback">{errors.fullName}</div>}
                    </div>
                  ) : (
                    <p className="mb-0">{formData.fullName || 'N/A'}</p>
                  )}
                </div>
                <div className="col-md-6 mb-3">
                  <label className="form-label fw-medium">
                    <Mail size={16} className="me-2" />
                    Email
                  </label>
                  {isEditing ? (
                    <div>
                      <input
                        type="email"
                        className={`form-control ${errors.email ? 'is-invalid' : ''}`}
                        value={formData.email}
                        onChange={(e) => handleInputChange('email', e.target.value)}
                        disabled={isVerified}
                      />
                      {errors.email && <div className="invalid-feedback">{errors.email}</div>}
                    </div>
                  ) : (
                    <div className="d-flex align-items-center gap-2">
                      <p className="mb-0">{formData.email || 'N/A'}</p>
                      {!isVerified && (
                        <button 
                          className="btn btn-sm btn-primary" 
                          onClick={handleSendOtp}
                          disabled={isSendingOtp}
                        >
                          {isSendingOtp ? 'Sending...' : 'Verify'}
                        </button>
                      )}
                    </div>
                  )}
                </div>
                <div className="col-md-6 mb-3">
                  <label className="form-label fw-medium">
                    <Phone size={16} className="me-2" />
                    Mobile
                  </label>
                  {isEditing ? (
                    <div>
                      <input
                        type="tel"
                        className={`form-control ${errors.mobile ? 'is-invalid' : ''}`}
                        value={formData.mobile}
                        onChange={(e) => handleInputChange('mobile', e.target.value)}
                      />
                      {errors.mobile && <div className="invalid-feedback">{errors.mobile}</div>}
                    </div>
                  ) : (
                    <p className="mb-0">{formData.mobile || 'N/A'}</p>
                  )}
                </div>
                <div className="col-md-6 mb-3">
                  <label className="form-label fw-medium">
                    <MapPin size={16} className="me-2" />
                    Address
                  </label>
                  {isEditing ? (
                    <div>
                      <input
                        type="text"
                        className={`form-control ${errors.address ? 'is-invalid' : ''}`}
                        value={formData.address}
                        onChange={(e) => handleInputChange('address', e.target.value)}
                      />
                      {errors.address && <div className="invalid-feedback">{errors.address}</div>}
                    </div>
                  ) : (
                    <p className="mb-0">{formData.address || 'N/A'}</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        
        
         {/* Password Change Section */}
      <div className="row mt-4">
        <div className="col-12">
          <div className="card shadow-sm border profile-card-enhanced">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                  <h4 className="h5 mb-1 fw-bold">Change Password</h4>
                  <p className="mb-0 text-muted">Update your account password</p>
                </div>
                <div className="d-flex align-items-center gap-2">
                  <Lock size={20} className="text-primary" />
                </div>
              </div>

              {showPasswordSuccess && (
                <div className="alert alert-success alert-dismissible fade show" role="alert">
                  <Check size={16} className="me-2" />
                  Password changed successfully!
                  <button type="button" className="btn-close" onClick={() => setShowPasswordSuccess(false)}></button>
                </div>
              )}

              <div className="row">
                <div className="col-md-6 mb-3">
                  <label className="form-label fw-medium">
                    <Lock size={16} className="me-2" />
                    New Password
                  </label>
                  <div className="input-group">
                    <input
                      type={showPasswords.new ? "text" : "password"}
                      className={`form-control ${passwordErrors.newPassword ? 'is-invalid' : ''}`}
                      value={passwordData.newPassword}
                      onChange={(e) => handlePasswordInputChange('newPassword', e.target.value)}
                      placeholder="Enter new password"
                    />
                    <button 
                      className="btn btn-outline-secondary" 
                      type="button"
                      onClick={() => togglePasswordVisibility('new')}
                    >
                      {showPasswords.new ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                  {passwordErrors.newPassword && <div className="invalid-feedback d-block">{passwordErrors.newPassword}</div>}
                </div>
                <div className="col-md-6 mb-3">
                  <label className="form-label fw-medium">
                    <Lock size={16} className="me-2" />
                    Confirm New Password
                  </label>
                  <div className="input-group">
                    <input
                      type={showPasswords.confirm ? "text" : "password"}
                      className={`form-control ${passwordErrors.confirmPassword ? 'is-invalid' : ''}`}
                      value={passwordData.confirmPassword}
                      onChange={(e) => handlePasswordInputChange('confirmPassword', e.target.value)}
                      placeholder="Confirm new password"
                    />
                    <button 
                      className="btn btn-outline-secondary" 
                      type="button"
                      onClick={() => togglePasswordVisibility('confirm')}
                    >
                      {showPasswords.confirm ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                  {passwordErrors.confirmPassword && <div className="invalid-feedback d-block">{passwordErrors.confirmPassword}</div>}
                </div>
              </div>

              <div className="d-flex justify-content-end gap-2">
                <button 
                  className="btn btn-primary"
                  onClick={handlePasswordChange}
                  disabled={isChangingPassword}
                >
                  {isChangingPassword ? 'Changing...' : 'Change Password'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
        </div>

        
      </div>

     

      <input type="file" ref={fileInputRef} onChange={handleFileChange} style={{display: 'none'}} accept="image/*" />
      
      {/* OTP Modal */}
      {showOtpModal && (
        <div className="modal show d-block" tabIndex="-1" style={{backgroundColor: 'rgba(0,0,0,0.5)'}}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Verify Email</h5>
                <button type="button" className="btn-close" onClick={handleCloseOtpModal}></button>
              </div>
              <div className="modal-body">
                <p className="text-center mb-4">Enter the 6-digit OTP sent to {formData.email}</p>
                <div className="d-flex justify-content-center gap-2 mb-4">
                  {otp.map((digit, index) => (
                    <input
                      key={index}
                      ref={otpInputRefs[index]}
                      type="text"
                      className="form-control text-center"
                      style={{width: '45px', height: '45px', fontSize: '1.2rem', fontWeight: 'bold'}}
                      maxLength={1}
                      value={digit}
                      onChange={(e) => handleOtpChange(index, e.target.value)}
                      onKeyDown={(e) => handleOtpKeyDown(index, e)}
                    />
                  ))}
                </div>
                <div className="d-flex justify-content-center gap-2">
                  <button 
                    className="btn btn-secondary" 
                    onClick={handleCloseOtpModal}
                    disabled={isVerifyingOtp}
                  >
                    Cancel
                  </button>
                  <button 
                    className="btn btn-primary" 
                    onClick={handleVerifyOtp}
                    disabled={isVerifyingOtp}
                  >
                    {isVerifyingOtp ? 'Verifying...' : 'Verify'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Profile;
